import { StoryblokServerComponent } from "@storyblok/react/rsc";
import { type CSSProperties, type ComponentProps, Fragment } from "react";
import { normalizeSize } from "~lib/components/normalize-size";
import { cn } from "~utils";
import { RichText } from "../rich-text";
import type { SectionBlockHeaderProps } from "./types";

function RenderTitle({ title }: Readonly<{ title: string }>) {
  return (
    <Fragment>
      {title.split("\\n").map((line, index, array) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
        <span key={index}>
          {line}
          {index < array.length - 1 && <br />}
        </span>
      ))}
    </Fragment>
  );
}

function GroupItems({ className, ...props }: Readonly<ComponentProps<"div">>) {
  return (
    <div
      className={cn(
        "flex max-w-max flex-wrap justify-center gap-sm md:justify-normal",
        className,
      )}
      {...props}
    />
  );
}

function SectionBlockHeader({
  blok,
  ...context
}: Readonly<SectionBlockHeaderProps>) {
  // FONT
  const leadingFont = blok.leadingFont || "t-prosi-xl";
  const titleFont = blok.titleFont || "t-strong-3xl";
  const textFont = blok.textFont || "t-strong-lg";

  // FONT COLOR
  const leadingColor = blok.leadingColor || "inherit";
  const titleColor = blok.titleColor || "inherit";
  const textColor = blok.textColor || "inherit";

  // GAP
  const gap = blok.gap || "gap-sm";
  const maxWidth = normalizeSize(blok.maxWidth) || 800;

  // ITEMS
  const hasTopItems = (blok.topItems?.length ?? 0) > 0;
  const hasBottomItems = (blok.bottomItems?.length ?? 0) > 0;

  // ALIGNMENT
  const alignment = `${blok.alignItems ?? ""}`;

  return (
    <div
      className={cn("flex w-full flex-col gap-lg", alignment)}
      style={
        {
          "--leading-color": leadingColor,
          "--title-color": titleColor,
          "--text-color": textColor,
          maxWidth,
        } as CSSProperties
      }
    >
      {/* TOP ITEMS */}
      {hasTopItems && (
        <GroupItems>
          {blok?.topItems?.map(nestedBlok => (
            <StoryblokServerComponent
              blok={nestedBlok}
              key={nestedBlok._uid}
              {...context}
            />
          ))}
        </GroupItems>
      )}

      {/* MIDDLE ITEMS */}
      <div className={cn("flex w-full flex-col", gap, alignment)}>
        {blok.leading && (
          <h4 className={cn("text-[var(--leading-color)]", leadingFont)}>
            {blok.leading}
          </h4>
        )}

        {blok.title && (
          <h2 className={cn("text-[var(--title-color)]", titleFont)}>
            <RenderTitle title={blok.title} />
          </h2>
        )}

        {blok.text && (
          <RichText
            data={blok.text}
            className={cn("text-[var(--text-color)]", textFont)}
            {...context}
          />
        )}

        {/* BOTTOM ITEMS */}
        {hasBottomItems && (
          <GroupItems>
            {blok?.bottomItems?.map(nestedBlok => (
              <StoryblokServerComponent
                blok={nestedBlok}
                key={nestedBlok._uid}
                {...context}
              />
            ))}
          </GroupItems>
        )}
      </div>
    </div>
  );
}

export { SectionBlockHeader };
